import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FadeUp, Stagger, StaggerItem } from '../components/Motion';
import { Download, ArrowRight, ShieldCheck, Sprout, Users, BarChart3, FileText, Globe2, CheckCircle2, Send } from 'lucide-react';
import api from '../lib/api';

const DOSSIER_URL = 'https://customer-assets.emergentagent.com/job_25f9bf8f-c5cb-4cac-8767-b1d65aca2574/artifacts/q6tfch7k_Rapport%20annuel%20lawatan%202025.pdf';
const HERO_IMG = 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/kbtklty8_IMG_7087.jpeg';

const FOCUS_KEYS = ['agroecology', 'training', 'biodiversity', 'women', 'processing', 'climate'];
const PARTNER_TYPES = ['funding', 'technical', 'research', 'ngo', 'media', 'other'];
const BUDGETS = ['lt50', '50-150', '150-500', 'gt500', 'inkind'];
const TIMELINES = ['short', 'mid', 'long', 'xlong'];

export default function Support() {
  const { t } = useTranslation();
  const items = t('support.why_items', { returnObjects: true });
  const types = t('support.types', { returnObjects: true });
  const impactItems = t('support.impact_items', { returnObjects: true });

  const [form, setForm] = useState({
    organization: '', contact_name: '', role: '', email: '', phone: '', country: '', website: '',
    partner_type: 'funding', focus_areas: [], budget_range: '', timeline: '', message: '',
  });
  const [status, setStatus] = useState({ sent: false, error: '', loading: false });

  const toggleFocus = (k) => {
    setForm((f) => ({
      ...f,
      focus_areas: f.focus_areas.includes(k) ? f.focus_areas.filter((x) => x !== k) : [...f.focus_areas, k],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ sent: false, error: '', loading: true });
    try {
      await api.post('/partnership-applications', form);
      setStatus({ sent: true, error: '', loading: false });
      window.scrollTo({ top: document.getElementById('apply')?.offsetTop - 100, behavior: 'smooth' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : t('support.error');
      setStatus({ sent: false, error: msg, loading: false });
    }
  };

  return (
    <main data-testid="support-page" className="pt-0">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
        </div>
        <div className="relative container-x max-w-7xl mx-auto pb-24 pt-40">
          <FadeUp>
            <p className="overline text-sun mb-6">{t('support.overline')}</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-bone leading-[0.95] tracking-tight whitespace-pre-line text-balance max-w-5xl">
              {t('support.title')}
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 text-lg lg:text-xl text-bone/85 max-w-2xl leading-relaxed">{t('support.sub')}</p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#apply" data-testid="cta-apply" className="btn-accent">
                {t('support.cta_apply')} <ArrowRight size={18} />
              </a>
              <a
                href={DOSSIER_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="cta-dossier"
                className="inline-flex items-center gap-2 text-bone border border-bone/40 hover:bg-bone hover:text-ink rounded-full px-7 py-3 font-medium transition-all"
              >
                <Download size={18} /> {t('support.cta_dossier')}
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* WHY */}
      <section className="section bg-bone grain">
        <div className="container-x max-w-7xl mx-auto">
          <div className="grid gap-10 lg:grid-cols-12 items-end mb-16">
            <FadeUp className="lg:col-span-7">
              <p className="overline mb-3">{t('support.why_overline')}</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight">{t('support.why_title')}</h2>
            </FadeUp>
          </div>
          <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[ShieldCheck, Sprout, BarChart3, FileText, Users, Globe2].map((Icon, i) => (
              <StaggerItem key={items[i]?.t || `why-${i}`}>
                <div className="bg-white border border-border-soft rounded-3xl p-8 hover-lift h-full" data-testid={`why-card-${i}`}>
                  <div className="w-14 h-14 grid place-items-center rounded-2xl bg-forest/8 text-forest mb-6">
                    <Icon size={26} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-heading text-2xl text-ink mb-3">{items[i]?.t}</h3>
                  <p className="text-ink-soft leading-relaxed">{items[i]?.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* PARTNERSHIP TYPES */}
      <section className="section bg-sand/50">
        <div className="container-x max-w-7xl mx-auto">
          <FadeUp>
            <p className="overline mb-3">{t('support.types_overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight max-w-3xl">{t('support.types_title')}</h2>
          </FadeUp>
          <Stagger className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {types.map((tp, i) => (
              <StaggerItem key={tp.t}>
                <div className="bg-white border-l-4 border-earth rounded-r-3xl p-7 h-full hover-lift" data-testid={`type-${i}`}>
                  <div className="font-mono text-xs text-earth mb-2">0{i + 1}</div>
                  <h3 className="font-heading text-2xl text-ink mb-2">{tp.t}</h3>
                  <p className="text-ink-soft leading-relaxed">{tp.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="section bg-forest text-bone">
        <div className="container-x max-w-7xl mx-auto grid gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <p className="overline text-sun mb-3">Engagements</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t('support.impact_title')}</h2>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7 lg:col-start-7">
            <ul className="grid gap-4">
              {impactItems.map((it) => (
                <li key={it} className="flex items-start gap-3 border-b border-bone/15 pb-4">
                  <CheckCircle2 size={22} className="text-sun shrink-0 mt-0.5" />
                  <span className="text-bone/90 text-lg">{it}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="section bg-bone scroll-mt-24">
        <div className="container-x max-w-5xl mx-auto">
          <FadeUp>
            <p className="overline mb-3">{t('support.form_overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight">{t('support.form_title')}</h2>
            <p className="mt-4 text-ink-soft text-lg max-w-2xl">{t('support.form_sub')}</p>
          </FadeUp>

          {status.sent ? (
            <FadeUp className="mt-12">
              <div className="bg-white border border-forest/20 rounded-3xl p-12 text-center" data-testid="application-success">
                <div className="w-16 h-16 grid place-items-center rounded-full bg-forest/10 text-forest mx-auto mb-6">
                  <CheckCircle2 size={32} />
                </div>
                <h3 className="font-heading text-3xl text-ink mb-2">{t('support.sent_title')}</h3>
                <p className="text-ink-soft text-lg max-w-md mx-auto">{t('support.sent_sub')}</p>
                <a href={DOSSIER_URL} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 text-forest border-b border-forest/40 hover:border-forest pb-1">
                  <Download size={16} /> {t('support.cta_dossier')}
                </a>
              </div>
            </FadeUp>
          ) : (
            <FadeUp className="mt-12">
              <form onSubmit={submit} className="bg-white border border-border-soft rounded-3xl p-8 sm:p-12 grid gap-5" data-testid="application-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={t('support.f_organization')} value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} required testid="app-organization" />
                  <Field label={t('support.f_contact_name')} value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} required testid="app-contact-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={t('support.f_role')} value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="app-role" />
                  <Field label={t('support.f_email')} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="app-email" />
                </div>
                <div className="grid sm:grid-cols-3 gap-5">
                  <Field label={t('support.f_phone')} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="app-phone" />
                  <Field label={t('support.f_country')} value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="app-country" />
                  <Field label={t('support.f_website')} value={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="app-website" />
                </div>

                <div className="grid gap-2">
                  <label className="overline">{t('support.f_partner_type')}</label>
                  <div className="flex flex-wrap gap-2">
                    {PARTNER_TYPES.map((k) => (
                      <button
                        type="button"
                        key={k}
                        onClick={() => setForm({ ...form, partner_type: k })}
                        data-testid={`app-type-${k}`}
                        className={`text-sm px-4 py-2 rounded-full border transition-all ${
                          form.partner_type === k ? 'bg-forest text-bone border-forest' : 'bg-bone text-ink-soft border-border-soft hover:border-forest'
                        }`}
                      >
                        {t(`support.partner_types.${k}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-2">
                  <label className="overline">{t('support.f_focus_areas')}</label>
                  <div className="flex flex-wrap gap-2">
                    {FOCUS_KEYS.map((k) => (
                      <button
                        type="button"
                        key={k}
                        onClick={() => toggleFocus(k)}
                        data-testid={`app-focus-${k}`}
                        className={`text-sm px-4 py-2 rounded-full border transition-all ${
                          form.focus_areas.includes(k) ? 'bg-earth text-white border-earth' : 'bg-bone text-ink-soft border-border-soft hover:border-earth'
                        }`}
                      >
                        {t(`support.focus.${k}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="grid gap-2">
                    <label className="overline">{t('support.f_budget')}</label>
                    <select
                      value={form.budget_range}
                      onChange={(e) => setForm({ ...form, budget_range: e.target.value })}
                      data-testid="app-budget"
                      className="rounded-xl border border-border-soft bg-bone px-4 py-3 outline-none focus:border-forest"
                    >
                      <option value="">—</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{t(`support.budgets.${b}`)}</option>)}
                    </select>
                  </div>
                  <div className="grid gap-2">
                    <label className="overline">{t('support.f_timeline')}</label>
                    <select
                      value={form.timeline}
                      onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                      data-testid="app-timeline"
                      className="rounded-xl border border-border-soft bg-bone px-4 py-3 outline-none focus:border-forest"
                    >
                      <option value="">—</option>
                      {TIMELINES.map((tl) => <option key={tl} value={tl}>{t(`support.timelines.${tl}`)}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid gap-2">
                  <label className="overline">{t('support.f_message')}</label>
                  <textarea
                    required rows={6}
                    value={form.message}
                    placeholder={t('support.f_message_ph')}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="app-message"
                    className="rounded-xl border border-border-soft bg-bone px-4 py-3 outline-none focus:border-forest resize-none"
                  />
                </div>

                {status.error && <p className="text-red-700 text-sm" data-testid="application-error">{status.error}</p>}

                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <button type="submit" disabled={status.loading} className="btn-primary disabled:opacity-60" data-testid="application-submit">
                    {status.loading ? '…' : <>{t('support.submit')} <Send size={16} /></>}
                  </button>
                  <a
                    href={DOSSIER_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-forest hover:text-ink border-b border-forest/40 pb-0.5"
                  >
                    <Download size={16} /> {t('support.cta_dossier')}
                  </a>
                </div>
              </form>
            </FadeUp>
          )}
        </div>
      </section>
    </main>
  );
}

function Field({ label, type = 'text', value, onChange, required, testid }) {
  return (
    <div className="grid gap-2">
      <label className="overline">{label}</label>
      <input
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="rounded-xl border border-border-soft bg-bone px-4 py-3 outline-none focus:border-forest"
      />
    </div>
  );
}
